import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../../server";
import "./AdminPlatformIdentityAudit.css";

const PRODUCT_OPTIONS = [
  { value: "", label: "All products" },
  { value: "buzzvibe", label: "MATRIX" },
  { value: "wallet", label: "Wallet" },
  { value: "console", label: "Console" },
  { value: "membership", label: "Membership" },
  { value: "shop", label: "Shop" },
  { value: "social", label: "Social" },
  { value: "game", label: "Game" },
  { value: "security", label: "Security" },
];

const formatDateTime = (value) => {
  if (!value) {
    return "Not available";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Not available";
  }

  return parsed.toLocaleString();
};

const formatList = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return "None";
  }

  return values.join(", ");
};

const buildQueryString = (params) => {
  const searchParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const normalized = String(value || "").trim();
    if (normalized) {
      searchParams.set(key, normalized);
    }
  });

  return searchParams.toString();
};

const AuditMetric = ({ label, value }) => (
  <div className="platform-identity-audit-metric">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const AuditTagList = ({ values = [], emptyLabel = "None" }) => {
  if (!Array.isArray(values) || values.length === 0) {
    return <span className="platform-identity-audit-muted">{emptyLabel}</span>;
  }

  return (
    <div className="platform-identity-audit-tag-list">
      {values.map((value) => (
        <span key={value} className="platform-identity-audit-tag">{value}</span>
      ))}
    </div>
  );
};

const AdminPlatformIdentityAudit = () => {
  const [recentFilters, setRecentFilters] = useState({ product: "console", tenantId: "" });
  const [lookupFilters, setLookupFilters] = useState({ email: "", handle: "", uniqueId: "" });
  const [recentResults, setRecentResults] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [selectedIdentity, setSelectedIdentity] = useState(null);
  const [resolvedBy, setResolvedBy] = useState("");
  const [lookupMessage, setLookupMessage] = useState("Inspect a recent sync or search by email, handle, or unique ID.");

  const loadRecentAuditUsers = async (filters = recentFilters) => {
    setRecentLoading(true);

    try {
      const queryString = buildQueryString({
        product: filters.product,
        tenantId: filters.tenantId,
        limit: 10,
      });
      const response = await axios.get(`${server}/platform-identity/audit/users?${queryString}`, {
        withCredentials: true,
      });
      setRecentResults(Array.isArray(response.data?.results) ? response.data.results : []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load recent platform sync activity.");
    } finally {
      setRecentLoading(false);
    }
  };

  const loadAuditLookup = async (filters) => {
    setLookupLoading(true);
    setLookupMessage("");

    try {
      const queryString = buildQueryString(filters);
      const response = await axios.get(`${server}/platform-identity/audit/lookup?${queryString}`, {
        withCredentials: true,
      });
      setSelectedIdentity(response.data?.identity || null);
      setResolvedBy(response.data?.resolvedBy || "lookup");
    } catch (error) {
      setSelectedIdentity(null);
      setResolvedBy("");

      if (error.response?.status === 404) {
        setLookupMessage("No MATRIX user matched that lookup.");
        return;
      }

      setLookupMessage("Audit lookup is temporarily unavailable.");
      toast.error(error.response?.data?.message || "Failed to load the platform identity audit lookup.");
    } finally {
      setLookupLoading(false);
    }
  };

  useEffect(() => {
    loadRecentAuditUsers(recentFilters);
  }, []);

  const handleRecentFilterChange = (event) => {
    const { name, value } = event.target;
    setRecentFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleLookupFilterChange = (event) => {
    const { name, value } = event.target;
    setLookupFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleRecentFilterSubmit = async (event) => {
    event.preventDefault();
    await loadRecentAuditUsers(recentFilters);
  };

  const handleLookupSubmit = async (event) => {
    event.preventDefault();

    const hasLookupValue = Object.values(lookupFilters).some((value) => String(value || "").trim());
    if (!hasLookupValue) {
      setLookupMessage("Enter at least one audit lookup value.");
      return;
    }

    await loadAuditLookup(lookupFilters);
  };

  const handleInspectUser = async (userId) => {
    await loadAuditLookup({ userId });
  };

  const entitlements = selectedIdentity?.entitlements || null;
  const productEntries = Object.entries(selectedIdentity?.products || {});

  return (
    <div className="platform-identity-audit">
      <header className="admin-page-hero">
        <div>
          <p className="admin-page-eyebrow">Platform Identity Audit</p>
          <h1>Cross-Product Identity Links</h1>
          <p className="admin-page-copy">Review the latest Wallet and Console sync activity, then inspect the MATRIX identity, entitlements, and linked product records behind each user.</p>
        </div>
      </header>

      <div className="platform-identity-audit-grid">
        <section className="platform-identity-audit-card">
          <div className="platform-identity-audit-card-header">
            <div>
              <p>Recent Sync Activity</p>
              <h2>Last 10 resolved platform links</h2>
            </div>
          </div>

          <form className="platform-identity-audit-toolbar" onSubmit={handleRecentFilterSubmit}>
            <label>
              <span>Product</span>
              <select name="product" value={recentFilters.product} onChange={handleRecentFilterChange}>
                {PRODUCT_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Tenant</span>
              <input
                type="text"
                name="tenantId"
                value={recentFilters.tenantId}
                onChange={handleRecentFilterChange}
                placeholder="default"
              />
            </label>

            <button type="submit" className="platform-identity-audit-button">Refresh</button>
          </form>

          {recentLoading ? (
            <div className="platform-identity-audit-empty">Loading recent audit results...</div>
          ) : recentResults.length === 0 ? (
            <div className="platform-identity-audit-empty">No recent synced identities matched this filter.</div>
          ) : (
            <div className="platform-identity-audit-table-wrap">
              <table className="platform-identity-audit-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Products</th>
                    <th>Links</th>
                    <th>Last Sync</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recentResults.map((result) => (
                    <tr key={result.userId}>
                      <td>
                        <strong>{result.identity?.user?.email || "Unknown user"}</strong>
                        <span>{result.identity?.user?.uniqueId || "No unique ID"}</span>
                      </td>
                      <td>{formatList(result.products)}</td>
                      <td>{result.linkCount || 0}</td>
                      <td>{formatDateTime(result.latestUpdatedAt)}</td>
                      <td>
                        <button
                          type="button"
                          className="platform-identity-audit-button platform-identity-audit-button--ghost"
                          onClick={() => handleInspectUser(result.userId)}
                        >
                          Inspect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="platform-identity-audit-card">
          <div className="platform-identity-audit-card-header">
            <div>
              <p>Identity Lookup</p>
              <h2>Resolve one MATRIX user</h2>
            </div>
          </div>

          <form className="platform-identity-audit-toolbar platform-identity-audit-toolbar--lookup" onSubmit={handleLookupSubmit}>
            <label>
              <span>Email</span>
              <input
                type="email"
                name="email"
                value={lookupFilters.email}
                onChange={handleLookupFilterChange}
                placeholder="console-link@example.com"
              />
            </label>

            <label>
              <span>Handle</span>
              <input
                type="text"
                name="handle"
                value={lookupFilters.handle}
                onChange={handleLookupFilterChange}
                placeholder="buzzhandle"
              />
            </label>

            <label>
              <span>Unique ID</span>
              <input
                type="text"
                name="uniqueId"
                value={lookupFilters.uniqueId}
                onChange={handleLookupFilterChange}
                placeholder="BV-000123"
              />
            </label>

            <button type="submit" className="platform-identity-audit-button">Run Lookup</button>
          </form>

          {lookupLoading ? (
            <div className="platform-identity-audit-empty">Loading identity snapshot...</div>
          ) : !selectedIdentity ? (
            <div className="platform-identity-audit-empty">{lookupMessage}</div>
          ) : (
            <div className="platform-identity-audit-profile">
              <div className="platform-identity-audit-profile-header">
                <div>
                  <p>Resolved By</p>
                  <h3>{resolvedBy || "lookup"}</h3>
                </div>
                <div>
                  <p>MATRIX User</p>
                  <h3>{selectedIdentity.user?.email || "Unknown user"}</h3>
                </div>
              </div>

              <div className="platform-identity-audit-metrics">
                <AuditMetric label="Linked Products" value={entitlements?.linkedProducts?.length || 0} />
                <AuditMetric label="Accessible Products" value={entitlements?.accessibleProducts?.length || 0} />
                <AuditMetric label="Roles" value={entitlements?.roles?.length || 0} />
                <AuditMetric label="Feature Flags" value={entitlements?.featureFlags?.length || 0} />
              </div>

              <div className="platform-identity-audit-section-grid">
                <article className="platform-identity-audit-panel">
                  <h4>Hub Identity</h4>
                  <dl>
                    <div><dt>Email</dt><dd>{selectedIdentity.user?.email || "Not available"}</dd></div>
                    <div><dt>Handle</dt><dd>{selectedIdentity.user?.handle || "Not available"}</dd></div>
                    <div><dt>Unique ID</dt><dd>{selectedIdentity.user?.uniqueId || "Not available"}</dd></div>
                    <div><dt>Role</dt><dd>{selectedIdentity.user?.role || "Not available"}</dd></div>
                  </dl>
                </article>

                <article className="platform-identity-audit-panel">
                  <h4>Entitlements</h4>
                  <dl>
                    <div><dt>Linked Products</dt><dd>{formatList(entitlements?.linkedProducts)}</dd></div>
                    <div><dt>Accessible Products</dt><dd>{formatList(entitlements?.accessibleProducts)}</dd></div>
                    <div><dt>Roles</dt><dd>{formatList(entitlements?.roles)}</dd></div>
                    <div><dt>Scopes</dt><dd>{formatList(entitlements?.scopes)}</dd></div>
                    <div><dt>Feature Flags</dt><dd>{formatList(entitlements?.featureFlags)}</dd></div>
                  </dl>
                </article>
              </div>

              <section className="platform-identity-audit-panel">
                <div className="platform-identity-audit-panel-header">
                  <h4>Membership + Subscriptions</h4>
                </div>
                <dl className="platform-identity-audit-inline-list">
                  <div><dt>Membership Status</dt><dd>{selectedIdentity.membership?.status || "Not available"}</dd></div>
                  <div><dt>Membership Tier</dt><dd>{selectedIdentity.membership?.tier || "Not available"}</dd></div>
                  <div><dt>Subscriptions</dt><dd>{selectedIdentity.subscriptions?.length || 0}</dd></div>
                </dl>
              </section>

              <section className="platform-identity-audit-panel">
                <div className="platform-identity-audit-panel-header">
                  <h4>Linked Product Records</h4>
                </div>
                {productEntries.length === 0 ? (
                  <div className="platform-identity-audit-empty">No linked products are recorded for this user.</div>
                ) : (
                  productEntries.map(([productKey, links]) => (
                    <div key={productKey} className="platform-identity-audit-product-group">
                      <div className="platform-identity-audit-product-header">
                        <h5>{productKey}</h5>
                        <span>{Array.isArray(links) ? links.length : 0} link(s)</span>
                      </div>
                      {(Array.isArray(links) ? links : []).map((link) => (
                        <article key={link.id} className="platform-identity-audit-link-card">
                          <div className="platform-identity-audit-link-card-grid">
                            <div>
                              <span className="platform-identity-audit-label">External Email</span>
                              <strong>{link.externalEmail || "Not available"}</strong>
                            </div>
                            <div>
                              <span className="platform-identity-audit-label">Tenant</span>
                              <strong>{link.tenantId || "default"}</strong>
                            </div>
                            <div>
                              <span className="platform-identity-audit-label">Sync Source</span>
                              <strong>{link.sync?.source || "Not available"}</strong>
                            </div>
                            <div>
                              <span className="platform-identity-audit-label">Last Sync</span>
                              <strong>{formatDateTime(link.sync?.lastSyncedAt)}</strong>
                            </div>
                          </div>
                          <div className="platform-identity-audit-link-card-meta">
                            <div>
                              <span className="platform-identity-audit-label">Roles</span>
                              <AuditTagList values={link.access?.roles} />
                            </div>
                            <div>
                              <span className="platform-identity-audit-label">Scopes</span>
                              <AuditTagList values={link.access?.scopes} />
                            </div>
                            <div>
                              <span className="platform-identity-audit-label">Feature Flags</span>
                              <AuditTagList values={link.entitlements?.featureFlags} />
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  ))
                )}
              </section>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminPlatformIdentityAudit;
